if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.currentIndex = 0;
        this.scrollFrame = null;
        this.railAnimationFrame = null;
        this.railAnimationTarget = null;
        this.imageRecoveryBound = new WeakSet();
        this.mediaStateObserver = null;
        this.thumbnailStateObserver = null;
        this.activeStateFrame = null;
        this.variantMediaTargetId = null;
        this.variantMediaSyncTimer = null;
        this.variantThumbnailPositionedId = null;

        /*
         * Separate from the active thumbnail.
         * The selected variant's featured thumbnail must remain physically
         * present in the rail even after the shopper views another media item.
         */
        this.currentVariantFeaturedThumbnail = null;

        this.railScrollStyleRestore = null;

        this.mainSwipeStart = null;
        this.touchSwipeStart = null;
        this.touchSwipeLast = null;
        this.touchSwipeHorizontal = false;
        this.mainSwipePointerId = null;
        this.mainSwipeDidMove = false;
        this.mainSwipeClickBlockUntil = 0;

        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onArrowClick = this.onArrowClick.bind(this);
        this.onThumbnailScroll = this.onThumbnailScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onWindowLoad = this.onWindowLoad.bind(this);

        this.onMainPointerDown = this.onMainPointerDown.bind(this);
        this.onMainPointerUp = this.onMainPointerUp.bind(this);
        this.onMainPointerCancel = this.onMainPointerCancel.bind(this);
        this.onMainTouchStart = this.onMainTouchStart.bind(this);
        this.onMainTouchMove = this.onMainTouchMove.bind(this);
        this.onMainTouchEnd = this.onMainTouchEnd.bind(this);
        this.onMainTouchCancel = this.onMainTouchCancel.bind(this);
        this.onMainClickCapture = this.onMainClickCapture.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;

        this.cacheElements();
        if (!this.viewerTrack || !this.thumbnailTrack) return;

        this.initialized = true;
        this.dataset.galleryReady = 'true';
        this.refreshItems();
        this.ensureArrowButtons();
        this.bindEvents();
        this.setInitialState();
        this.observeMediaState();
        this.observeThumbnailState();

        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewerTrack);
        this.resizeObserver.observe(this.thumbnailTrack);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.refreshItems();
            this.updateRailPresentation();
          });
        });

        window.addEventListener('load', this.onWindowLoad, { once: true });
        window.setTimeout(this.onWindowLoad, 300);
      }

      disconnectedCallback() {
        this.thumbnailTrack?.removeEventListener('click', this.onThumbnailClick);
        this.thumbnailTrack?.removeEventListener('scroll', this.onThumbnailScroll);

        this.viewerTrack?.removeEventListener('pointerdown', this.onMainPointerDown);
        this.viewerTrack?.removeEventListener('pointerup', this.onMainPointerUp);
        this.viewerTrack?.removeEventListener('pointercancel', this.onMainPointerCancel);
        this.viewerTrack?.removeEventListener('touchstart', this.onMainTouchStart);
        this.viewerTrack?.removeEventListener('touchmove', this.onMainTouchMove);
        this.viewerTrack?.removeEventListener('touchend', this.onMainTouchEnd);
        this.viewerTrack?.removeEventListener('touchcancel', this.onMainTouchCancel);
        this.viewerTrack?.removeEventListener('click', this.onMainClickCapture, true);

        this.previousArrow?.removeEventListener('click', this.onArrowClick, true);
        this.nextArrow?.removeEventListener('click', this.onArrowClick, true);

        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('load', this.onWindowLoad);

        if (this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        } else {
          this.desktopQuery.removeListener(this.onBreakpointChange);
        }

        this.resizeObserver?.disconnect();
        this.mediaStateObserver?.disconnect();
        this.thumbnailStateObserver?.disconnect();

        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        if (this.railAnimationFrame) cancelAnimationFrame(this.railAnimationFrame);
        this.restoreRailProgrammaticScrollStyles();
        if (this.activeStateFrame) cancelAnimationFrame(this.activeStateFrame);
        if (this.variantMediaSyncTimer) clearTimeout(this.variantMediaSyncTimer);

        this.initialized = false;
      }

      cacheElements() {
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.viewer = this.querySelector('[id^="GalleryViewer"]');
        this.viewerTrack = this.querySelector('[id^="Slider-Gallery"], [data-gallery-track="viewer"]');
        this.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        this.thumbnailTrack = this.querySelector('[id^="Slider-Thumbnails"], [data-gallery-track="thumbnails"]');
      }

      refreshItems() {
        this.mediaItems = Array.from(this.viewerTrack?.querySelectorAll('[data-media-id]') || []);
        this.thumbnailItems = Array.from(this.thumbnailTrack?.querySelectorAll('[data-target]') || []);
        this.bindImageRecovery();
      }

      observeMediaState() {
        this.mediaStateObserver?.disconnect();

        this.mediaStateObserver = new MutationObserver((mutations) => {
          const mediaStateChanged = mutations.some(
            (mutation) =>
              mutation.type === 'childList' ||
              (
                mutation.type === 'attributes' &&
                ['class', 'aria-hidden', 'style', 'src', 'srcset'].includes(
                  mutation.attributeName
                )
              )
          );

          if (!mediaStateChanged) return;

          this.scheduleActiveThumbnailSync();
        });

        this.mediaStateObserver.observe(this.viewerTrack, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'aria-hidden', 'style', 'src', 'srcset'],
        });
      }

      scheduleActiveThumbnailSync() {
        if (this.activeStateFrame) {
          cancelAnimationFrame(this.activeStateFrame);
        }

        this.activeStateFrame = requestAnimationFrame(() => {
          this.activeStateFrame = null;
          this.syncActiveThumbnailFromMainMedia();
        });
      }

      observeThumbnailState() {
        this.thumbnailStateObserver?.disconnect();

        this.thumbnailStateObserver = new MutationObserver((mutations) => {
          const changed = mutations.some(
            (mutation) =>
              mutation.type === 'childList' ||
              (
                mutation.type === 'attributes' &&
                ['style', 'src', 'srcset', 'data-target'].includes(
                  mutation.attributeName
                )
              )
          );

          if (!changed) return;
          this.refreshItems();
          this.scheduleActiveThumbnailSync();
        });

        this.thumbnailStateObserver.observe(this.thumbnailTrack, {
          childList: true,
          subtree: true,
          attributes: true,
          /*
           * Gallery-owned class changes happen during every arrow frame.
           * Watching them caused variant sync to repeatedly pull the rail
           * back to the selected variant after a variant change.
           */
          attributeFilter: ['style', 'src', 'srcset', 'data-target'],
        });
      }

      syncActiveThumbnailFromMainMedia() {
        this.refreshItems();

        /*
         * A variant change is authoritative for a short window. Dawn and
         * third-party variant scripts can update the main image and its
         * classes in separate frames, so keep the requested media's thumbnail
         * selected until that update settles.
         */
        if (this.variantMediaTargetId) {
          const variantMedia =
            this.resolveMediaElement(this.variantMediaTargetId) ||
            this.getActiveMediaElement();

          if (variantMedia) {
            const variantThumbnail =
              this.getThumbnail(this.variantMediaTargetId) ||
              this.findThumbnailForMedia(variantMedia);

            if (variantThumbnail) {
              this.setCurrentVariantFeaturedThumbnail(variantThumbnail);
              this.updateActiveThumbnail(variantThumbnail);

              const index = this.mediaItems.indexOf(variantMedia);
              if (index >= 0) this.currentIndex = index;

              /*
               * Bring the selected variant thumbnail into view once.
               * Keep the active state authoritative afterwards, but never
               * continuously pull the rail back while arrows are scrolling.
               */
              const positionKey =
                variantThumbnail.dataset.target ||
                variantThumbnail.id ||
                this.variantMediaTargetId;

              if (this.variantThumbnailPositionedId !== positionKey) {
                this.ensureThumbnailVisible(variantThumbnail, 'auto');
                this.variantThumbnailPositionedId = positionKey;
              }

              this.updateCounter();
              this.updateRailPresentation();
              return;
            }
          }
        }

        const activeMedia =
          this.mediaItems.find(
            (item) =>
              item.classList.contains('is-active') &&
              item.getAttribute('aria-hidden') !== 'true'
          ) ||
          this.mediaItems.find(
            (item) => item.getAttribute('aria-hidden') === 'false'
          ) ||
          this.mediaItems.find((item) => item.classList.contains('is-active')) ||
          this.getVisuallyActiveMedia();

        if (!activeMedia) return;

        const index = this.mediaItems.indexOf(activeMedia);
        if (index >= 0) this.currentIndex = index;

        const activeThumbnail =
          this.findThumbnailForMedia(activeMedia) ||
          this.getThumbnail(activeMedia.dataset.mediaId);

        this.updateActiveThumbnail(activeThumbnail);
        this.updateCounter();
        this.updateRailPresentation();
      }

      getVisuallyActiveMedia() {
        return (
          this.mediaItems.find((item) => {
            const style = window.getComputedStyle(item);
            return (
              style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              Number.parseFloat(style.opacity || '1') > 0
            );
          }) || null
        );
      }

      normalizeImageUrl(rawUrl) {
        if (!rawUrl) return '';

        try {
          const url = new URL(String(rawUrl), window.location.origin);
          let pathname = decodeURIComponent(url.pathname).toLowerCase();

          pathname = pathname
            .replace(
              /_(pico|icon|thumb|small|compact|medium|large|grande|master)(?=\.[a-z0-9]+$)/,
              ''
            )
            .replace(
              /_\d+x(?:\d+)?(?:_crop_[a-z]+)?(?=\.[a-z0-9]+$)/,
              ''
            )
            .replace(
              /_(?:crop_[a-z]+_)?\d+x\d*(?=\.[a-z0-9]+$)/,
              ''
            );

          return pathname;
        } catch (error) {
          return String(rawUrl)
            .split('?')[0]
            .toLowerCase()
            .replace(
              /_\d+x(?:\d+)?(?:_crop_[a-z]+)?(?=\.[a-z0-9]+$)/,
              ''
            );
        }
      }

      getImageKeys(image) {
        if (!image) return new Set();

        const urls = [
          image.currentSrc,
          image.getAttribute('src'),
          image.dataset.src,
          image.dataset.originalSrc,
        ].filter(Boolean);

        const srcset =
          image.getAttribute('srcset') ||
          image.dataset.srcset ||
          '';

        srcset
          .split(',')
          .map((entry) => entry.trim().split(/\s+/)[0])
          .filter(Boolean)
          .forEach((url) => urls.push(url));

        return new Set(
          urls
            .map((url) => this.normalizeImageUrl(url))
            .filter(Boolean)
        );
      }

      normalizeImageKey(image) {
        return Array.from(this.getImageKeys(image))[0] || '';
      }

      findThumbnailForMedia(media) {
        if (!media) return null;

        const direct = this.getThumbnail(media.dataset.mediaId);
        if (direct) return direct;

        const mediaImage = media.querySelector('img');
        const mediaKeys = this.getImageKeys(mediaImage);
        const mediaAlt = String(mediaImage?.alt || '')
          .trim()
          .toLowerCase();

        if (mediaKeys.size) {
          const sourceMatch = this.thumbnailItems.find((item) => {
            const thumbnailImage = item.querySelector('img');
            const thumbKeys = this.getImageKeys(thumbnailImage);

            return Array.from(thumbKeys).some((key) => mediaKeys.has(key));
          });

          if (sourceMatch) return sourceMatch;
        }

        if (mediaAlt) {
          const altMatches = this.thumbnailItems.filter((item) => {
            const thumbnailAlt = String(item.querySelector('img')?.alt || '')
              .trim()
              .toLowerCase();

            return thumbnailAlt && thumbnailAlt === mediaAlt;
          });

          if (altMatches.length === 1) return altMatches[0];
        }

        return null;
      }

      findMediaForThumbnail(thumbnail) {
        if (!thumbnail) return null;

        const target = thumbnail.dataset.target;
        const direct = this.resolveMediaElement(target);
        if (direct) return direct;

        const thumbImage = thumbnail.querySelector('img');
        const thumbKeys = this.getImageKeys(thumbImage);

        if (thumbKeys.size) {
          const sourceMatch = this.mediaItems.find((media) => {
            const mediaKeys = this.getImageKeys(media.querySelector('img'));
            return Array.from(mediaKeys).some((key) => thumbKeys.has(key));
          });

          if (sourceMatch) return sourceMatch;
        }

        return null;
      }

      resolveMediaElement(mediaId) {
        const requestedId = String(mediaId || '')
          .trim()
          .replace(/^#/, '');

        if (!requestedId) return null;

        const requestedNumericId = requestedId.split('-').pop();

        return (
          this.mediaItems.find(
            (item) =>
              item.dataset.mediaId === requestedId ||
              item.id === requestedId ||
              item.id === `Slide-${requestedId}`
          ) ||
          this.mediaItems.find((item) => {
            const itemId = String(item.dataset.mediaId || '');
            return (
              requestedNumericId &&
              itemId.split('-').pop() === requestedNumericId
            );
          }) ||
          null
        );
      }

      ensureArrowButtons() {
        this.previousArrow = this.thumbnails?.querySelector(
          'button[name="previous"], [data-gallery-previous]'
        );
        this.nextArrow = this.thumbnails?.querySelector(
          'button[name="next"], [data-gallery-next]'
        );

        if (!this.previousArrow) {
          this.previousArrow = document.createElement('button');
          this.previousArrow.type = 'button';
          this.previousArrow.name = 'previous';
          this.previousArrow.setAttribute('aria-label', 'Previous thumbnails');
          this.thumbnails?.prepend(this.previousArrow);
        }

        if (!this.nextArrow) {
          this.nextArrow = document.createElement('button');
          this.nextArrow.type = 'button';
          this.nextArrow.name = 'next';
          this.nextArrow.setAttribute('aria-label', 'Next thumbnails');
          this.thumbnails?.append(this.nextArrow);
        }

        [
          [this.previousArrow, 'previous'],
          [this.nextArrow, 'next'],
        ].forEach(([button, direction]) => {
          if (!button) return;

          button.type = 'button';
          button.hidden = false;
          button.removeAttribute('hidden');
          button.removeAttribute('disabled');
          button.removeAttribute('style');

          /*
           * Remove Dawn's generic slider classes. Those classes were adding
           * their own rotations, hidden states and positional offsets such as
           * bottom: -50px.
           */
          button.classList.remove(
            'slider-button',
            'slider-button--prev',
            'slider-button--next',
            'small-hide',
            'medium-hide',
            'large-up-hide'
          );

          button.classList.add(
            'product-gallery__rail-arrow',
            `product-gallery__rail-arrow--${direction}`
          );
          button.dataset.galleryRailArrow = direction;
          button.innerHTML = `<svg aria-hidden="true" role="presentation" class="product-gallery__rail-arrow-svg" viewBox="0 0 292 492" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.986 7.86402L8.75798 23.98C3.68998 29.052 0.897983 35.8 0.897983 43.012C0.897983 50.22 3.68998 56.976 8.75798 62.048L192.606 245.888L8.554 429.94C3.486 435.004 0.697996 441.76 0.697996 448.968C0.697996 456.176 3.486 462.936 8.554 468.004L24.682 484.124C35.17 494.62 52.254 494.62 62.742 484.124L282.666 264.988C287.73 259.924 291.298 253.176 291.298 245.904L291.298 245.82C291.298 238.608 287.726 231.86 282.666 226.796L63.338 7.86402C58.274 2.79202 51.322 0.00800542 44.114 5.73162e-06C36.902 6.04687e-06 30.046 2.79202 24.986 7.86402Z" fill="currentColor"></path></svg>`;
        });
      }

      bindEvents() {
        this.thumbnailTrack.addEventListener('click', this.onThumbnailClick);
        this.thumbnailTrack.addEventListener('scroll', this.onThumbnailScroll, { passive: true });

        /*
         * Main image swiping is gesture-driven instead of relying on the
         * viewer's native scroll position because only one main media item is
         * displayed at a time.
         */
        this.viewerTrack.addEventListener('pointerdown', this.onMainPointerDown, { passive: true });
        this.viewerTrack.addEventListener('pointerup', this.onMainPointerUp, { passive: true });
        this.viewerTrack.addEventListener('pointercancel', this.onMainPointerCancel, { passive: true });

        /*
         * iOS/Safari touch fallback. The main viewer displays only one media
         * at a time, so native horizontal scrolling cannot perform the swap.
         */
        this.viewerTrack.addEventListener('touchstart', this.onMainTouchStart, { passive: true });
        this.viewerTrack.addEventListener('touchmove', this.onMainTouchMove, { passive: false });
        this.viewerTrack.addEventListener('touchend', this.onMainTouchEnd, { passive: true });
        this.viewerTrack.addEventListener('touchcancel', this.onMainTouchCancel, { passive: true });

        /*
         * Capture click before Dawn's modal opener sees it. This completely
         * disables image zoom/modal opening without affecting video/model
         * controls.
         */
        this.viewerTrack.addEventListener('click', this.onMainClickCapture, true);

        this.previousArrow?.addEventListener('click', this.onArrowClick, true);
        this.nextArrow?.addEventListener('click', this.onArrowClick, true);

        window.addEventListener('resize', this.onResize, { passive: true });

        if (this.desktopQuery.addEventListener) {
          this.desktopQuery.addEventListener('change', this.onBreakpointChange);
        } else {
          this.desktopQuery.addListener(this.onBreakpointChange);
        }
      }

      setInitialState() {
        this.refreshItems();

        const activeMedia = this.mediaItems.find((item) => item.classList.contains('is-active'));
        const activeThumbnail = this.thumbnailItems.find((item) =>
          item.querySelector('button')?.hasAttribute('aria-current')
        );

        let initialIndex = activeMedia ? this.mediaItems.indexOf(activeMedia) : -1;

        if (initialIndex < 0 && activeThumbnail?.dataset.target) {
          initialIndex = this.getMediaIndex(activeThumbnail.dataset.target);
        }

        this.selectIndex(initialIndex >= 0 ? initialIndex : 0, {
          announce: false,
          playMedia: false,
          scrollViewer: false,
        });
      }

      isImageMediaTarget(target) {
        const mediaItem = target?.closest?.('[data-media-id]');
        if (!mediaItem || !this.viewerTrack.contains(mediaItem)) return false;

        /*
         * Only product images participate in swipe/zoom suppression.
         * Video, 3D/model and external-media controls retain their native
         * interactions.
         */
        return Boolean(
          mediaItem.querySelector(
            '.product__modal-opener--image, img'
          )
        ) &&
          !mediaItem.querySelector(
            'video, model-viewer, iframe, .deferred-media'
          );
      }

      setCurrentVariantFeaturedThumbnail(thumbnail) {
        /*
         * Dawn hides variant thumbnails other than the first one with:
         *
         * .thumbnail-list_item--variant:not(:first-child) { display: none; }
         *
         * Active-state visibility alone is therefore not enough: as soon as
         * the shopper swipes/clicks to another media item, aria-current and
         * is-gallery-selected move away and the variant's first image vanishes.
         *
         * Keep one independent marker for the CURRENT VARIANT's featured
         * thumbnail. Active selection can move freely without hiding it.
         */
        this.thumbnailItems.forEach((item) => {
          item.classList.remove('is-current-variant-featured');
        });

        if (!thumbnail) {
          this.currentVariantFeaturedThumbnail = null;
          return;
        }

        thumbnail.classList.add('is-current-variant-featured');
        this.currentVariantFeaturedThumbnail = thumbnail;
      }

      clearVariantMediaLock() {
        /*
         * Stop forcing the variant media to remain ACTIVE after a manual
         * thumbnail click/swipe, but intentionally keep
         * is-current-variant-featured on its thumbnail. That marker is replaced
         * only when another variant is selected.
         */
        this.variantMediaTargetId = null;
        this.variantThumbnailPositionedId = null;

        if (this.variantMediaSyncTimer) {
          clearTimeout(this.variantMediaSyncTimer);
          this.variantMediaSyncTimer = null;
        }
      }

      getActiveMediaElement() {
        return (
          this.mediaItems.find(
            (item) =>
              item.classList.contains('is-active') &&
              item.getAttribute('aria-hidden') !== 'true'
          ) ||
          this.mediaItems.find((item) => item.classList.contains('is-active')) ||
          this.getVisuallyActiveMedia() ||
          null
        );
      }

      getVisibleThumbnailItems() {
        return this.thumbnailItems.filter((item) => {
          const style = window.getComputedStyle(item);
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            item.offsetWidth > 0 &&
            item.offsetHeight > 0
          );
        });
      }

      swipeToAdjacentMedia(direction) {
        this.refreshItems();

        const visibleThumbnails = this.getVisibleThumbnailItems();
        if (!visibleThumbnails.length) return false;

        const activeMedia = this.getActiveMediaElement();
        const selectedThumbnail =
          this.thumbnailItems.find((item) =>
            item.classList.contains('is-gallery-selected')
          ) ||
          this.thumbnailItems.find((item) =>
            item.querySelector('[aria-current="true"]')
          ) ||
          this.findThumbnailForMedia(activeMedia);

        let currentThumbIndex = visibleThumbnails.indexOf(selectedThumbnail);

        if (currentThumbIndex < 0 && activeMedia) {
          const fallbackThumb =
            this.getThumbnail(activeMedia.dataset.mediaId) ||
            this.findThumbnailForMedia(activeMedia);

          currentThumbIndex = visibleThumbnails.indexOf(fallbackThumb);
        }

        if (currentThumbIndex < 0) {
          currentThumbIndex = direction > 0 ? -1 : visibleThumbnails.length;
        }

        const targetThumbnail =
          visibleThumbnails[currentThumbIndex + direction];

        if (!targetThumbnail) return false;

        const targetMedia = this.findMediaForThumbnail(targetThumbnail);
        if (!targetMedia) return false;

        const targetIndex = this.mediaItems.indexOf(targetMedia);
        if (targetIndex < 0) return false;

        this.clearVariantMediaLock();

        this.selectIndex(targetIndex, {
          announce: true,
          playMedia: true,
          scrollViewer: false,
        });

        /*
         * Force the exact thumbnail selected by the gesture. This avoids a
         * stale variant/media ID overriding it during the same frame.
         */
        this.updateActiveThumbnail(targetThumbnail);
        this.ensureThumbnailVisible(targetThumbnail, 'smooth');

        return true;
      }

      performMainSwipe(deltaX, deltaY, elapsed) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        const isHorizontalSwipe =
          absX >= 24 &&
          absX > absY * 1.05 &&
          elapsed <= 1400;

        if (!isHorizontalSwipe) return false;

        this.mainSwipeDidMove = true;
        this.mainSwipeClickBlockUntil = performance.now() + 500;

        const direction = deltaX < 0 ? 1 : -1;
        return this.swipeToAdjacentMedia(direction);
      }

      onMainTouchStart(event) {
        if (this.desktopQuery.matches) return;
        if (event.touches.length !== 1) return;
        if (!this.isImageMediaTarget(event.target)) return;

        const touch = event.touches[0];

        this.touchSwipeStart = {
          x: touch.clientX,
          y: touch.clientY,
          time: performance.now(),
        };
        this.touchSwipeLast = {
          x: touch.clientX,
          y: touch.clientY,
        };
        this.touchSwipeHorizontal = false;
      }

      onMainTouchMove(event) {
        if (!this.touchSwipeStart || event.touches.length !== 1) return;

        const touch = event.touches[0];
        this.touchSwipeLast = {
          x: touch.clientX,
          y: touch.clientY,
        };

        const deltaX = touch.clientX - this.touchSwipeStart.x;
        const deltaY = touch.clientY - this.touchSwipeStart.y;

        if (
          !this.touchSwipeHorizontal &&
          Math.abs(deltaX) >= 10 &&
          Math.abs(deltaX) > Math.abs(deltaY) * 1.05
        ) {
          this.touchSwipeHorizontal = true;
        }

        /*
         * Once clearly horizontal, claim the gesture so the browser/theme
         * cannot steal it for another slider. Vertical scrolling is untouched.
         */
        if (this.touchSwipeHorizontal) {
          event.preventDefault();
        }
      }

      onMainTouchEnd(event) {
        if (!this.touchSwipeStart) return;

        const touch =
          event.changedTouches?.[0] ||
          this.touchSwipeLast;

        if (touch) {
          const deltaX = touch.clientX - this.touchSwipeStart.x;
          const deltaY = touch.clientY - this.touchSwipeStart.y;
          const elapsed = performance.now() - this.touchSwipeStart.time;

          this.performMainSwipe(deltaX, deltaY, elapsed);
        }

        this.touchSwipeStart = null;
        this.touchSwipeLast = null;
        this.touchSwipeHorizontal = false;
      }

      onMainTouchCancel() {
        this.touchSwipeStart = null;
        this.touchSwipeLast = null;
        this.touchSwipeHorizontal = false;
      }

      onMainPointerDown(event) {
        if (event.pointerType === 'touch') return;
        if (!this.isImageMediaTarget(event.target)) return;

        /*
         * Primary pointer only. touch-action: pan-y in CSS leaves vertical
         * page scrolling native while still letting us recognize a horizontal
         * image swipe.
         */
        if (event.isPrimary === false) return;

        this.mainSwipePointerId = event.pointerId;
        this.mainSwipeDidMove = false;
        this.mainSwipeStart = {
          x: event.clientX,
          y: event.clientY,
          time: performance.now(),
        };
      }

      onMainPointerUp(event) {
        if (event.pointerType === 'touch') return;

        if (
          !this.mainSwipeStart ||
          this.mainSwipePointerId !== event.pointerId
        ) {
          this.resetMainSwipe();
          return;
        }

        const deltaX = event.clientX - this.mainSwipeStart.x;
        const deltaY = event.clientY - this.mainSwipeStart.y;
        const elapsed = performance.now() - this.mainSwipeStart.time;

        this.performMainSwipe(deltaX, deltaY, elapsed);
        this.resetMainSwipe();
      }

      onMainPointerCancel() {
        this.resetMainSwipe();
      }

      resetMainSwipe() {
        this.mainSwipeStart = null;
        this.mainSwipePointerId = null;
      }

      onMainClickCapture(event) {
        if (!this.isImageMediaTarget(event.target)) return;

        const isZoomTrigger = Boolean(
          event.target.closest(
            '.product__media-toggle, .product__modal-opener--image, modal-opener'
          )
        );

        /*
         * Block both the synthetic click that follows a swipe and normal
         * click/tap zoom on product images.
         */
        if (
          isZoomTrigger ||
          performance.now() < this.mainSwipeClickBlockUntil
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
        }
      }

      onThumbnailClick(event) {
        const button = event.target.closest('button');
        const thumbnail = event.target.closest('[data-target]');

        if (!button || !thumbnail || !this.thumbnailTrack.contains(thumbnail)) return;

        event.preventDefault();

        const index = this.getMediaIndex(thumbnail.dataset.target);
        if (index < 0) return;

        this.clearVariantMediaLock();

        /* Only a thumbnail click swaps the main media. */
        this.selectIndex(index, {
          announce: true,
          playMedia: true,
          scrollViewer: false,
        });
      }

      onArrowClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const button = event.currentTarget;
        if (!button || button.classList.contains('is-gallery-arrow-hidden')) return;

        /*
         * Arrows move the thumbnail rail only. Preserve the selected variant
         * thumbnail as active, but never auto-scroll back to it after this.
         */
        if (this.variantMediaTargetId && !this.variantThumbnailPositionedId) {
          const activeThumbnail = this.thumbnailItems.find((item) =>
            item.classList.contains('is-gallery-selected')
          );

          this.variantThumbnailPositionedId =
            activeThumbnail?.dataset.target ||
            activeThumbnail?.id ||
            this.variantMediaTargetId;
        }

        const direction =
          button.dataset.galleryRailArrow === 'previous' ? -1 : 1;

        const vertical = this.desktopQuery.matches;
        const step = this.getThumbnailStep();
        const current = vertical
          ? this.thumbnailTrack.scrollTop
          : this.thumbnailTrack.scrollLeft;
        const maxScroll = vertical
          ? Math.max(0, this.thumbnailTrack.scrollHeight - this.thumbnailTrack.clientHeight)
          : Math.max(0, this.thumbnailTrack.scrollWidth - this.thumbnailTrack.clientWidth);

        const baseTarget =
          Number.isFinite(this.railAnimationTarget)
            ? this.railAnimationTarget
            : current;

        const target = Math.max(
          0,
          Math.min(baseTarget + direction * step, maxScroll)
        );

        if (Math.abs(target - current) < 0.5) {
          this.railAnimationTarget = null;
          this.updateRailPresentation();
          return;
        }

        this.animateRailTo(target);
      }

      onThumbnailScroll() {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateRailPresentation();
        });
      }

      onResize() {
        this.refreshItems();
        this.updateRailPresentation();
      }

      onWindowLoad() {
        this.refreshItems();
        this.updateRailPresentation();
      }

      onBreakpointChange() {
        this.refreshItems();
        this.applyMediaVisibility();
        this.updateRailPresentation();
      }

      selectIndex(index, { announce = false, playMedia = true, scrollViewer = false } = {}) {
        this.refreshItems();
        if (!this.mediaItems.length) return;

        const safeIndex = Math.max(0, Math.min(index, this.mediaItems.length - 1));
        const activeMedia = this.mediaItems[safeIndex];
        if (!activeMedia) return;

        this.currentIndex = safeIndex;

        this.mediaItems.forEach((item, itemIndex) => {
          const selected = itemIndex === safeIndex;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-hidden', selected ? 'false' : 'true');
        });

        const activeThumbnail =
          this.getThumbnail(activeMedia.dataset.mediaId) ||
          this.findThumbnailForMedia(activeMedia);

        this.updateActiveThumbnail(activeThumbnail);
        this.ensureMediaReady(activeMedia);

        if (scrollViewer && !this.desktopQuery.matches) {
          this.viewerTrack.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
          });
        }

        this.updateCounter();
        this.updateRailPresentation();

        if (playMedia) this.playActiveMedia(activeMedia);
        if (announce && activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      applyMediaVisibility() {
        this.mediaItems.forEach((item, index) => {
          const selected = index === this.currentIndex;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-hidden', selected ? 'false' : 'true');
        });
      }

      updateActiveThumbnail(activeThumbnail) {
        this.thumbnailItems.forEach((item) => {
          const selected = item === activeThumbnail;
          item.classList.toggle('is-gallery-selected', selected);

          const button = item.querySelector('button');
          if (!button) return;

          if (selected) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
      }

      ensureThumbnailVisible(thumbnail, behavior = 'smooth') {
        if (!thumbnail || !this.thumbnailTrack) return;

        const vertical = this.desktopQuery.matches;
        const trackRect = this.thumbnailTrack.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();

        const startPadding = vertical ? 58 : 46;
        const endPadding = vertical ? 58 : 46;

        let delta = 0;

        if (vertical) {
          const safeTop = trackRect.top + startPadding;
          const safeBottom = trackRect.bottom - endPadding;

          if (thumbRect.top < safeTop) {
            delta = thumbRect.top - safeTop;
          } else if (thumbRect.bottom > safeBottom) {
            delta = thumbRect.bottom - safeBottom;
          }

          if (Math.abs(delta) > 1) {
            this.thumbnailTrack.scrollTo({
              top: this.thumbnailTrack.scrollTop + delta,
              behavior: this.prefersReducedMotion() ? 'auto' : behavior,
            });
          }
        } else {
          const safeLeft = trackRect.left + startPadding;
          const safeRight = trackRect.right - endPadding;

          if (thumbRect.left < safeLeft) {
            delta = thumbRect.left - safeLeft;
          } else if (thumbRect.right > safeRight) {
            delta = thumbRect.right - safeRight;
          }

          if (Math.abs(delta) > 1) {
            this.thumbnailTrack.scrollTo({
              left: this.thumbnailTrack.scrollLeft + delta,
              behavior: this.prefersReducedMotion() ? 'auto' : behavior,
            });
          }
        }
      }

      updateRailPresentation() {
        this.refreshItems();
        if (!this.thumbnailTrack || !this.thumbnailItems.length) return;

        this.thumbnailItems.forEach((item) => {
          item.classList.remove(
            'is-under-previous-arrow',
            'is-under-next-arrow'
          );
        });

        const vertical = this.desktopQuery.matches;
        const position = vertical
          ? this.thumbnailTrack.scrollTop
          : this.thumbnailTrack.scrollLeft;
        const maxScroll = vertical
          ? Math.max(
              0,
              this.thumbnailTrack.scrollHeight -
                this.thumbnailTrack.clientHeight
            )
          : Math.max(
              0,
              this.thumbnailTrack.scrollWidth -
                this.thumbnailTrack.clientWidth
            );

        const edgeTolerance = 2;
        let showPrevious = position > edgeTolerance;
        let showNext = position < maxScroll - edgeTolerance;

        const activeThumbnail = this.thumbnailItems.find((item) =>
          item.classList.contains('is-gallery-selected')
        );

        const previousCovered = showPrevious
          ? this.getThumbnailCoveredByArrow(this.previousArrow, 'previous')
          : null;
        const nextCovered = showNext
          ? this.getThumbnailCoveredByArrow(this.nextArrow, 'next')
          : null;

        /*
         * Preserve the approved behavior: when the active thumbnail is
         * physically beneath an arrow, that arrow disappears rather than
         * blurring the active selection.
         */
        /*
         * Desktop keeps the approved "active thumb hides its covering arrow"
         * behavior. On mobile, arrows are navigation affordances for the
         * physical rail and must appear immediately after a manual swipe.
         */
        if (this.desktopQuery.matches) {
          if (previousCovered && previousCovered === activeThumbnail) {
            showPrevious = false;
          }

          if (nextCovered && nextCovered === activeThumbnail) {
            showNext = false;
          }
        }

        this.setArrowVisible(this.previousArrow, showPrevious);
        this.setArrowVisible(this.nextArrow, showNext);

        if (showPrevious && previousCovered && previousCovered !== activeThumbnail) {
          previousCovered.classList.add('is-under-previous-arrow');
        }

        if (showNext && nextCovered && nextCovered !== activeThumbnail) {
          nextCovered.classList.add('is-under-next-arrow');
        }
      }

      getThumbnailCoveredByArrow(button, direction) {
        if (!button || !this.thumbnailTrack) return null;

        const arrowRect = button.getBoundingClientRect();
        const trackRect = this.thumbnailTrack.getBoundingClientRect();
        const vertical = this.desktopQuery.matches;

        let bestItem = null;
        let bestOverlap = 0;

        this.thumbnailItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return;

          const axisOverlap = vertical
            ? Math.max(
                0,
                Math.min(rect.bottom, arrowRect.bottom) -
                  Math.max(rect.top, arrowRect.top)
              )
            : Math.max(
                0,
                Math.min(rect.right, arrowRect.right) -
                  Math.max(rect.left, arrowRect.left)
              );

          const crossOverlap = vertical
            ? Math.max(
                0,
                Math.min(rect.right, arrowRect.right) -
                  Math.max(rect.left, arrowRect.left)
              )
            : Math.max(
                0,
                Math.min(rect.bottom, arrowRect.bottom) -
                  Math.max(rect.top, arrowRect.top)
              );

          const overlap = axisOverlap * Math.max(1, crossOverlap);
          if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestItem = item;
          }
        });

        if (bestItem) return bestItem;

        /*
         * Fallback for layouts where the arrow sits just outside the
         * scrollable track: use the physically nearest visible thumbnail.
         */
        const visible = this.getVisibleThumbnailItems();
        if (!visible.length) return null;

        return direction === 'previous'
          ? visible.reduce((closest, item) => {
              if (!closest) return item;
              const rect = item.getBoundingClientRect();
              const closestRect = closest.getBoundingClientRect();
              const edge = vertical ? trackRect.top : trackRect.left;
              const distance = vertical
                ? Math.abs(rect.top - edge)
                : Math.abs(rect.left - edge);
              const closestDistance = vertical
                ? Math.abs(closestRect.top - edge)
                : Math.abs(closestRect.left - edge);
              return distance < closestDistance ? item : closest;
            }, null)
          : visible.reduce((closest, item) => {
              if (!closest) return item;
              const rect = item.getBoundingClientRect();
              const closestRect = closest.getBoundingClientRect();
              const edge = vertical ? trackRect.bottom : trackRect.right;
              const distance = vertical
                ? Math.abs(rect.bottom - edge)
                : Math.abs(rect.right - edge);
              const closestDistance = vertical
                ? Math.abs(closestRect.bottom - edge)
                : Math.abs(closestRect.right - edge);
              return distance < closestDistance ? item : closest;
            }, null);
      }

      prepareRailForProgrammaticScroll() {
        if (!this.thumbnailTrack) return;

        this.restoreRailProgrammaticScrollStyles();

        const track = this.thumbnailTrack;
        const previous = {
          behaviorValue: track.style.getPropertyValue('scroll-behavior'),
          behaviorPriority: track.style.getPropertyPriority('scroll-behavior'),
          snapValue: track.style.getPropertyValue('scroll-snap-type'),
          snapPriority: track.style.getPropertyPriority('scroll-snap-type'),
        };

        /*
         * The final CSS intentionally uses smooth scrolling + mandatory snap.
         * Temporarily switch both off while the JS arrow animation owns the
         * rail, then restore them after the target settles.
         */
        track.style.setProperty('scroll-behavior', 'auto', 'important');
        track.style.setProperty('scroll-snap-type', 'none', 'important');

        this.railScrollStyleRestore = () => {
          if (!this.thumbnailTrack) return;

          if (previous.behaviorValue) {
            this.thumbnailTrack.style.setProperty(
              'scroll-behavior',
              previous.behaviorValue,
              previous.behaviorPriority
            );
          } else {
            this.thumbnailTrack.style.removeProperty('scroll-behavior');
          }

          if (previous.snapValue) {
            this.thumbnailTrack.style.setProperty(
              'scroll-snap-type',
              previous.snapValue,
              previous.snapPriority
            );
          } else {
            this.thumbnailTrack.style.removeProperty('scroll-snap-type');
          }

          this.railScrollStyleRestore = null;
        };
      }

      restoreRailProgrammaticScrollStyles() {
        if (typeof this.railScrollStyleRestore !== 'function') return;

        const restore = this.railScrollStyleRestore;
        this.railScrollStyleRestore = null;
        restore();
      }

      animateRailTo(target) {
        if (!this.thumbnailTrack) return;

        if (this.railAnimationFrame) {
          cancelAnimationFrame(this.railAnimationFrame);
          this.railAnimationFrame = null;
        }

        this.prepareRailForProgrammaticScroll();
        this.railAnimationTarget = target;

        const vertical = this.desktopQuery.matches;
        const start = vertical
          ? this.thumbnailTrack.scrollTop
          : this.thumbnailTrack.scrollLeft;
        const distance = target - start;

        const finish = () => {
          this.railAnimationFrame = null;
          this.railAnimationTarget = null;

          if (vertical) this.thumbnailTrack.scrollTop = target;
          else this.thumbnailTrack.scrollLeft = target;

          this.updateRailPresentation();

          requestAnimationFrame(() => {
            this.restoreRailProgrammaticScrollStyles();
            this.updateRailPresentation();
          });
        };

        if (this.prefersReducedMotion() || Math.abs(distance) < 1) {
          finish();
          return;
        }

        const duration = 230;
        const startTime = performance.now();
        const easeOutCubic = (progress) =>
          1 - Math.pow(1 - progress, 3);

        const frame = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          const value = start + distance * easeOutCubic(progress);

          if (vertical) this.thumbnailTrack.scrollTop = value;
          else this.thumbnailTrack.scrollLeft = value;

          this.updateRailPresentation();

          if (progress < 1) {
            this.railAnimationFrame = requestAnimationFrame(frame);
            return;
          }

          finish();
        };

        this.railAnimationFrame = requestAnimationFrame(frame);
      }

      getVisibleThumbnailItems() {
        const trackRect = this.thumbnailTrack.getBoundingClientRect();
        const vertical = this.desktopQuery.matches;
        const tolerance = 2;

        return this.thumbnailItems.filter((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return false;

          if (vertical) {
            return rect.bottom > trackRect.top + tolerance && rect.top < trackRect.bottom - tolerance;
          }

          return rect.right > trackRect.left + tolerance && rect.left < trackRect.right - tolerance;
        });
      }

      setArrowVisible(button, visible) {
        if (!button) return;

        button.hidden = false;
        button.removeAttribute('hidden');
        button.removeAttribute('disabled');
        button.classList.toggle(
          'is-gallery-arrow-hidden',
          !visible
        );
        button.setAttribute(
          'aria-hidden',
          visible ? 'false' : 'true'
        );
        button.setAttribute(
          'aria-disabled',
          visible ? 'false' : 'true'
        );
        button.tabIndex = visible ? 0 : -1;

        button.style.setProperty('display', 'flex', 'important');
        button.style.setProperty(
          'opacity',
          visible ? '1' : '0',
          'important'
        );
        button.style.setProperty(
          'visibility',
          visible ? 'visible' : 'hidden',
          'important'
        );
        button.style.setProperty(
          'pointer-events',
          visible ? 'auto' : 'none',
          'important'
        );
      }

      getThumbnailStep() {
        const vertical = this.desktopQuery.matches;

        const visibleItems = this.getVisibleThumbnailItems()
          .slice()
          .sort((a, b) =>
            vertical
              ? a.offsetTop - b.offsetTop
              : a.offsetLeft - b.offsetLeft
          );

        const positiveDistances = [];

        for (let index = 1; index < visibleItems.length; index += 1) {
          const previous = visibleItems[index - 1];
          const current = visibleItems[index];

          const difference = vertical
            ? current.offsetTop - previous.offsetTop
            : current.offsetLeft - previous.offsetLeft;

          if (difference > 1) positiveDistances.push(difference);
        }

        if (positiveDistances.length) {
          return Math.min(...positiveDistances);
        }

        const firstItem = visibleItems[0];
        if (!firstItem) return 96;

        const style = window.getComputedStyle(this.thumbnailTrack);
        const gap =
          Number.parseFloat(vertical ? style.rowGap : style.columnGap) || 0;

        return (vertical ? firstItem.offsetHeight : firstItem.offsetWidth) + gap;
      }

      getMediaIndex(mediaId) {
        if (!mediaId) return -1;
        return this.mediaItems.findIndex((item) => item.dataset.mediaId === mediaId);
      }

      getThumbnail(mediaId) {
        if (!mediaId) return null;

        const requestedId = String(mediaId)
          .trim()
          .replace(/^#/, '');
        const requestedNumericId = requestedId.split('-').pop();

        const exact = this.thumbnailItems.find((item) => {
          const button = item.querySelector('button');
          const values = [
            item.dataset.target,
            item.dataset.mediaId,
            item.id,
            button?.dataset.target,
            button?.dataset.mediaId,
            button?.getAttribute('aria-controls'),
          ]
            .filter(Boolean)
            .map(String);

          return values.some(
            (value) =>
              value === requestedId ||
              value === `Slide-Thumbnails-${requestedId}` ||
              value === `Slide-${requestedId}`
          );
        });

        if (exact) return exact;

        return (
          this.thumbnailItems.find((item) => {
            const button = item.querySelector('button');
            const values = [
              item.dataset.target,
              item.dataset.mediaId,
              item.id,
              button?.dataset.target,
              button?.dataset.mediaId,
              button?.getAttribute('aria-controls'),
            ].filter(Boolean);

            return values.some(
              (value) =>
                requestedNumericId &&
                String(value).split('-').pop() === requestedNumericId
            );
          }) || null
        );
      }

      ensureMediaReady(media) {
        const image = media?.querySelector('img');
        if (!image) return;

        image.loading = 'eager';

        try {
          image.fetchPriority = 'high';
        } catch (error) {
          /* Older browsers may not expose fetchPriority. */
        }

        if (image.complete && image.naturalWidth === 0) {
          this.recoverImage(image);
        }
      }

      bindImageRecovery() {
        const images = [
          ...(this.viewerTrack?.querySelectorAll('img') || []),
          ...(this.thumbnailTrack?.querySelectorAll('img') || []),
        ];

        images.forEach((image) => {
          if (this.imageRecoveryBound.has(image)) return;
          this.imageRecoveryBound.add(image);

          image.addEventListener('error', () => {
            this.recoverImage(image);
          });
        });
      }

      recoverImage(image) {
        if (!image || image.dataset.galleryRecoveryTried === 'true') return;

        image.dataset.galleryRecoveryTried = 'true';
        image.dataset.galleryRecovering = 'true';

        const candidates = [];
        const srcset = image.getAttribute('srcset') || '';

        srcset.split(',').forEach((entry) => {
          const url = entry.trim().split(/\s+/)[0];
          if (url) candidates.push(url);
        });

        const mediaItem = image.closest('[data-media-id]');
        const thumbnailItem = image.closest('[data-target]');

        if (mediaItem?.dataset.mediaId) {
          const counterpart = this.getThumbnail(mediaItem.dataset.mediaId)?.querySelector('img');
          const url = counterpart?.currentSrc || counterpart?.getAttribute('src');
          if (url) candidates.unshift(url);
        } else if (thumbnailItem?.dataset.target) {
          const mediaIndex = this.getMediaIndex(thumbnailItem.dataset.target);
          const counterpart =
            mediaIndex >= 0
              ? this.mediaItems[mediaIndex]?.querySelector('img')
              : null;
          const url = counterpart?.currentSrc || counterpart?.getAttribute('src');
          if (url) candidates.unshift(url);
        }

        const current = image.currentSrc || image.getAttribute('src') || '';
        const fallback = candidates.find((url) => url && url !== current);

        if (!fallback) {
          image.removeAttribute('data-gallery-recovering');
          return;
        }

        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = fallback;

        const clearRecovering = () => {
          image.removeAttribute('data-gallery-recovering');
        };

        image.addEventListener('load', clearRecovering, { once: true });
        window.setTimeout(clearRecovering, 800);
      }

      /* Public API used after a product variant changes. */
      setActiveMedia(mediaId, prepend = false) {
        this.refreshItems();

        const media =
          this.resolveMediaElement(mediaId) ||
          this.mediaItems[0];

        if (!media) return;

        const index = this.mediaItems.indexOf(media);
        const resolvedTargetId = media.dataset.mediaId || String(mediaId || '');

        this.variantMediaTargetId = resolvedTargetId;
        this.variantThumbnailPositionedId = null;

        /*
         * Revealing a variant thumbnail changes rail geometry and snap points.
         * Discard any arrow target calculated against the old layout.
         */
        if (this.railAnimationFrame) {
          cancelAnimationFrame(this.railAnimationFrame);
          this.railAnimationFrame = null;
        }
        this.railAnimationTarget = null;
        this.restoreRailProgrammaticScrollStyles();

        if (this.variantMediaSyncTimer) {
          clearTimeout(this.variantMediaSyncTimer);
          this.variantMediaSyncTimer = null;
        }

        /*
         * Keep this variant target authoritative until the shopper manually
         * chooses/swipes another media item. This prevents later Dawn DOM
         * updates from stripping the selected thumbnail state.
         */

        this.preventStickyHeader();

        this.selectIndex(index, {
          announce: true,
          playMedia: true,
          scrollViewer: false,
        });

        const requestedThumbnail =
          this.getThumbnail(resolvedTargetId) ||
          this.getThumbnail(mediaId) ||
          this.findThumbnailForMedia(media);

        if (requestedThumbnail) {
          this.setCurrentVariantFeaturedThumbnail(requestedThumbnail);
          this.updateActiveThumbnail(requestedThumbnail);
          this.ensureThumbnailVisible(requestedThumbnail, 'auto');
          this.variantThumbnailPositionedId =
            requestedThumbnail.dataset.target ||
            requestedThumbnail.id ||
            resolvedTargetId;
        }

        requestAnimationFrame(() => {
          this.syncActiveThumbnailFromMainMedia();

          requestAnimationFrame(() => {
            this.syncActiveThumbnailFromMainMedia();
          });
        });

        window.setTimeout(() => this.syncActiveThumbnailFromMainMedia(), 120);
        window.setTimeout(() => this.syncActiveThumbnailFromMainMedia(), 360);
        window.setTimeout(() => this.syncActiveThumbnailFromMainMedia(), 900);
      }

      updateCounter() {
        const current = this.viewer?.querySelector('.slider-counter--current');
        const total = this.viewer?.querySelector('.slider-counter--total');

        if (current) current.textContent = String(this.currentIndex + 1);
        if (total) total.textContent = String(this.mediaItems.length);
      }

      announceLiveRegion(activeItem, position) {
        if (!this.liveRegion || !position) return;

        const message = window.accessibilityStrings?.imageAvailable
          ? window.accessibilityStrings.imageAvailable.replace('[index]', position)
          : `Image ${position} is now available`;

        this.liveRegion.setAttribute('aria-hidden', 'false');
        this.liveRegion.textContent = message;
        window.setTimeout(() => this.liveRegion?.setAttribute('aria-hidden', 'true'), 2000);
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        activeItem.querySelector('.deferred-media')?.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  );
}
